import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ILocalization, LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import {
  PropertyAdditionalInfoViewModel,
  PropertyInfoViewModel,
  PropertyManagerViewModel,
  PropertyStructureDetailsViewModel
} from 'src/common/models';
import { QuestionAnswersViewModel } from 'src/common/models/hotel/hotel-details-types';

import { UitkCard, UitkCardContent } from 'uitk-react-cards';

import { AwardsAndAffiliations } from './internals/awards-and-affiliations';
import { FindAnswersInput } from 'components/infosite/find-answers/find-answers-input';
import { ImportantInfo } from './internals/important-info';
import { NavigationRefs } from 'components/infosite/navigation/navigation-refs';
import { PropertyAllInclusive } from './internals/property-all-inclusive';
import { PropertyDescription } from './internals/property-description';
import { PropertyInfoSection } from 'components/infosite/policies/property-info-section/index';
import { PropertyManager } from './internals/property-manager';
import { PropertyPolicies } from './internals/property-policies';
import { RenovationsAndClosures } from './internals/renovations-and-closures';

interface PoliciesProps {
  additionalInfo?: PropertyAdditionalInfoViewModel;
  analytics?: AnalyticsStore;
  propertyInfo?: PropertyInfoViewModel;
  hrefNextPage?: string;
  l10n?: ILocalization;
  questionAnswersViewModel?: QuestionAnswersViewModel;
  propertyManagerViewModel?: PropertyManagerViewModel;
  propertyStructureDetailsViewModel?: PropertyStructureDetailsViewModel;
}

export type LocalizedPoliciesProps = LocalizedComponentProps & PoliciesProps;

@withLocalization
export class Policies extends React.Component<LocalizedPoliciesProps> {
  public render() {
    const {
      additionalInfo,
      analytics,
      propertyInfo,
      l10n,
      hrefNextPage,
      questionAnswersViewModel,
      propertyManagerViewModel,
      propertyStructureDetailsViewModel
    } = this.props;

    return (
      <UitkCard className="policies all-y-margin-three">
        <UitkCardContent>
          <Viewport>
            <ViewSmall>
              <GetDefaultView
                additionalInfo={additionalInfo}
                analytics={analytics}
                propertyInfo={propertyInfo}
                l10n={l10n}
                hrefNextPage={hrefNextPage}
                questionAnswersViewModel={questionAnswersViewModel}
                propertyManagerViewModel={propertyManagerViewModel}
                propertyStructureDetailsViewModel={propertyStructureDetailsViewModel}
              />
            </ViewSmall>
            <ViewLarge>
              <GetLargeView
                additionalInfo={additionalInfo}
                analytics={analytics}
                propertyInfo={propertyInfo}
                l10n={l10n}
                questionAnswersViewModel={questionAnswersViewModel}
                propertyManagerViewModel={propertyManagerViewModel}
                propertyStructureDetailsViewModel={propertyStructureDetailsViewModel}
              />
            </ViewLarge>
          </Viewport>
        </UitkCardContent>
      </UitkCard>
    );
  }
}

const GetDefaultView = ({
  additionalInfo,
  analytics,
  propertyInfo,
  l10n,
  hrefNextPage,
  questionAnswersViewModel,
  propertyManagerViewModel,
  propertyStructureDetailsViewModel
}: PoliciesProps) => {
  const policies = propertyInfo && propertyInfo.policies;
  const petsPolicy = additionalInfo && additionalInfo.petsPolicy;
  const importantInfo = propertyInfo && propertyInfo.importantInfo;
  const shouldMention = additionalInfo && additionalInfo.shouldMention;
  const propertyAKA = additionalInfo && additionalInfo.propertyAlternativeName;
  const propertyDescription = additionalInfo && additionalInfo.propertyDescription;
  const renovationsAndClosuresDescription = additionalInfo && additionalInfo.renovationsAndClosuresDescription;
  const propertyAllInclusive = additionalInfo && additionalInfo.allInclusive;
  const propertyAwardsAndAffiliations = additionalInfo && additionalInfo.propertyAwardsAndAffiliations;

  const propertyAmenities = additionalInfo && additionalInfo.propertyAmenitiesSection;
  const propertyAmenitiesToggleMobile = propertyAmenities && propertyAmenities.toggleHidden && propertyAmenities.toggleHidden.mobile;
  const roomAmenities = additionalInfo && additionalInfo.roomAmenitiesSection;
  const roomAmenitiesToggleMobile = roomAmenities && roomAmenities.toggleHidden && roomAmenities.toggleHidden.mobile;
  const nearbyAmenities = additionalInfo && additionalInfo.nearbyThingsSection;
  const nearbyAmenitiesToggleMobile = nearbyAmenities && nearbyAmenities.toggleHidden && nearbyAmenities.toggleHidden.mobile;

  return (
    <React.Fragment>
      <FindAnswersInput questionAnswersViewModel={questionAnswersViewModel} topPadding={true} />
      <PropertyDescription propertyDescription={propertyDescription} propertyStructureDetails={propertyStructureDetailsViewModel} />
      <PropertyAllInclusive analytics={analytics} propertyAllInclusive={propertyAllInclusive} />
      <AwardsAndAffiliations propertyAwardsAndAffiliations={propertyAwardsAndAffiliations} />
      <PropertyManager propertyManager={propertyManagerViewModel} />
      <RenovationsAndClosures renovationsAndClosuresDescription={renovationsAndClosuresDescription} />
      <PropertyPolicies analytics={analytics} l10n={l10n} petsPolicy={petsPolicy} policies={policies} />
      <PropertyInfoSection
        analytics={analytics}
        isToggleHidden={propertyAmenitiesToggleMobile}
        propertyInfoSection={propertyAmenities}
        navigationRef={NavigationRefs.Amenities}
      />
      <PropertyInfoSection analytics={analytics} isToggleHidden={roomAmenitiesToggleMobile} propertyInfoSection={roomAmenities} />
      <PropertyInfoSection analytics={analytics} isToggleHidden={nearbyAmenitiesToggleMobile} propertyInfoSection={nearbyAmenities} />
      <ImportantInfo
        analytics={analytics}
        importantInfo={importantInfo}
        l10n={l10n}
        shouldMention={shouldMention}
        propertyAKA={propertyAKA}
        hrefNextPage={hrefNextPage}
        isToggleHidden={true}
      />
      <FindAnswersInput questionAnswersViewModel={questionAnswersViewModel} />
    </React.Fragment>
  );
};

const GetLargeView = ({
  additionalInfo,
  analytics,
  propertyInfo,
  l10n,
  questionAnswersViewModel,
  propertyManagerViewModel,
  propertyStructureDetailsViewModel
}: PoliciesProps) => {
  const policies = propertyInfo && propertyInfo.policies;
  const petsPolicy = additionalInfo && additionalInfo.petsPolicy;
  const importantInfo = propertyInfo && propertyInfo.importantInfo;
  const shouldMention = additionalInfo && additionalInfo.shouldMention;
  const propertyAKA = additionalInfo && additionalInfo.propertyAlternativeName;
  const propertyDescription = additionalInfo && additionalInfo.propertyDescription;
  const renovationsAndClosuresDescription = additionalInfo && additionalInfo.renovationsAndClosuresDescription;
  const propertyAllInclusive = additionalInfo && additionalInfo.allInclusive;
  const propertyAwardsAndAffiliations = additionalInfo && additionalInfo.propertyAwardsAndAffiliations;

  const propertyAmenities = additionalInfo && additionalInfo.propertyAmenitiesSection;
  const propertyAmenitiesToggleDesktop = propertyAmenities && propertyAmenities.toggleHidden && propertyAmenities.toggleHidden.desktop;
  const roomAmenities = additionalInfo && additionalInfo.roomAmenitiesSection;
  const roomAmenitiesToggleDesktop = roomAmenities && roomAmenities.toggleHidden && roomAmenities.toggleHidden.desktop;
  const nearbyAmenities = additionalInfo && additionalInfo.nearbyThingsSection;
  const nearbyAmenitiesToggleDesktop = nearbyAmenities && nearbyAmenities.toggleHidden && nearbyAmenities.toggleHidden.desktop;

  return (
    <React.Fragment>
      <div className="uitk-cell all-cell-1-2 all-x-padding-three">
        <FindAnswersInput questionAnswersViewModel={questionAnswersViewModel} topPadding={true} />
        <PropertyDescription propertyDescription={propertyDescription} propertyStructureDetails={propertyStructureDetailsViewModel} />
        <PropertyAllInclusive analytics={analytics} propertyAllInclusive={propertyAllInclusive} isToggleHidden={true} />
        <AwardsAndAffiliations propertyAwardsAndAffiliations={propertyAwardsAndAffiliations} />
        <PropertyManager propertyManager={propertyManagerViewModel} />
        <RenovationsAndClosures renovationsAndClosuresDescription={renovationsAndClosuresDescription} />
        <PropertyPolicies analytics={analytics} isToggleHidden={true} l10n={l10n} petsPolicy={petsPolicy} policies={policies} />
        <ImportantInfo
          analytics={analytics}
          importantInfo={importantInfo}
          l10n={l10n}
          shouldMention={shouldMention}
          propertyAKA={propertyAKA}
          isToggleHidden={true}
        />
      </div>
      <div className="uitk-cell all-cell-fill all-x-padding-three">
        <PropertyInfoSection
          analytics={analytics}
          isToggleHidden={propertyAmenitiesToggleDesktop}
          propertyInfoSection={propertyAmenities}
          navigationRef={NavigationRefs.Amenities}
        />
        <PropertyInfoSection analytics={analytics} isToggleHidden={roomAmenitiesToggleDesktop} propertyInfoSection={roomAmenities} />
        <PropertyInfoSection analytics={analytics} isToggleHidden={nearbyAmenitiesToggleDesktop} propertyInfoSection={nearbyAmenities} />
        <FindAnswersInput questionAnswersViewModel={questionAnswersViewModel} />
      </div>
    </React.Fragment>
  );
};
